import { AlkLogger } from '@alk/core';
import { AUTHENTICATION_FACTORY, IAuthenticationFactory } from '../authentication';
import { BACKGROUND_SERVICE, STOPPABLE_SERVICE } from '..';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { COMMUNICATION_SERVICE, ICommunicationService } from '../communication';
import { DATA_TRANSFER_SERVICE, IDataTransferService } from '../data-transfer';
import { EConnectionState } from '../../enums';
import { filter, takeUntil } from 'rxjs/operators';
import { IBackgroundService, ISessionDisposable } from '../../interfaces';
import { inject, injectable } from 'inversify';
import { ISessionLifecycle } from './session-lifecycle.interface';
import { lazyMultiInject } from '../container.ioc';
import { referenceEqualerCurried } from '../../helpers/object-utils';
import { stopDisposableService } from './session-lifecycle.utils';

@injectable()
export class SessionLifecycleService implements ISessionLifecycle {

    @inject(AUTHENTICATION_FACTORY) private readonly authFactory: IAuthenticationFactory;
    @inject(COMMUNICATION_SERVICE) private readonly communicationSrv: ICommunicationService;
    @lazyMultiInject(STOPPABLE_SERVICE) private readonly stoppableServices: Array<ISessionDisposable>;
    @lazyMultiInject(BACKGROUND_SERVICE) private readonly independantWorkers: Array<IBackgroundService>;
    @inject(DATA_TRANSFER_SERVICE) private readonly dataTransferService: IDataTransferService;
    protected readonly destroy$ = new Subject<boolean>();

    private readonly _state$ = new BehaviorSubject<EConnectionState>('disconnected');

    destroyObservable = (): Observable<boolean> => this.destroy$.asObservable();

    stop(): void {

        AlkLogger.debug(`[SessionLifecycleServiceService#stop] session is about to stop`);

        this.stoppableServices.forEach(stopDisposableService);

        this.destroy$.next(true);

        this.communicationSrv.stop();

        this._state$.next('disconnected');
    }

    async start(): Promise<void> {
        AlkLogger.debug(`[SessionLifecycleServiceService#start] session is about to start`);
        const authSrv = this.authFactory();

        if (await authSrv?.isLogged()) {

            await this.communicationSrv.start();

            this._state$.next('connected');

            this.dataTransferService.connectionStateObserver().pipe(
                takeUntil(this.destroy$),
                filter(referenceEqualerCurried('disconnected')),
            ).subscribe(() => {
                AlkLogger.info('[SessionLifecycleServiceService] LOGOUT');
                authSrv.logout();
            });

            this.independantWorkers.forEach(worker => worker.start());
        }
    }
}
